import { useEffect, useMemo, useState, useCallback } from 'react';
import type { Editor } from '@tiptap/react';
import type * as Y from 'yjs';
import { Flex, Text, Input, IconButton, Icon } from '@nula/core-ui';
import { FloatingPanel } from '@/components/floating-panel/floating-panel';
import { useAuthStore } from '@/stores/use-auth-store';
import {
  createThread,
  addReply,
  setResolved,
  deleteThread,
  getThread,
  type CommentThread,
} from '@/lib/comments/comment-threads';
import type { ShowCommentDetail } from './comment-extension';
import styles from './editor-comments.module.css';

export interface EditorCommentsProps {
  editor: Editor;
  doc: Y.Doc;
}

/**
 * Threaded comments UI. Two flows:
 *
 *  1. Add — a `nula-editor-add-comment` window event (dispatched by the bubble
 *     menu) creates a thread in the Yjs store and applies the `comment` mark over
 *     the current selection, then opens the panel for the first reply.
 *  2. View/reply — clicking commented text (a `[data-comment-id]` span) opens a
 *     floating thread panel: all replies, a reply input, resolve/reopen, delete.
 *
 * Thread content syncs via comment-threads.ts (Yjs `comments` map).
 */
export function EditorComments({ editor, doc }: EditorCommentsProps) {
  const user = useAuthStore((s) => s.user);
  const author = user?.name || user?.email || 'You';

  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  // Pending selection range for a NOT-YET-created thread (the add flow). The
  // thread + mark are created only when the first comment text is submitted.
  const [pendingRange, setPendingRange] = useState<{ from: number; to: number } | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [reply, setReply] = useState('');
  // Bumped on Yjs comments-map changes to re-read the open thread.
  const [version, setVersion] = useState(0);

  // Re-render when the comments map changes (local edits + remote sync).
  useEffect(() => {
    const map = doc.getMap('editor-comment-threads');
    const onChange = () => setVersion((v) => v + 1);
    map.observeDeep(onChange);
    return () => map.unobserveDeep(onChange);
  }, [doc]);

  // Add-comment: remember the selection range + open an empty panel. The thread
  // is created (and the mark applied) only when the first comment is submitted.
  useEffect(() => {
    const onAdd = () => {
      const { from, to } = editor.state.selection;
      if (from === to) return; // need a non-empty selection
      const coords = editor.view.coordsAtPos(to);
      setPos({ top: coords.bottom + 6, left: coords.left });
      setPendingRange({ from, to });
      setOpenThreadId(null);
      setReply('');
    };
    window.addEventListener('nula-editor-add-comment', onAdd);
    return () => window.removeEventListener('nula-editor-add-comment', onAdd);
  }, [editor]);

  // Open a thread when a commented range is clicked. The CommentMark's
  // handleClick ProseMirror plugin reads the threadId from the DOCUMENT MODEL
  // (not the DOM, which may lack data-comment-id) and dispatches this event with
  // the threadId + position — deterministic, no stale DOM-listener race.
  useEffect(() => {
    const onShow = (e: Event) => {
      const { threadId, top, left } = (e as CustomEvent<ShowCommentDetail>).detail;
      setPos({ top, left });
      setPendingRange(null);
      setOpenThreadId(threadId);
      setReply('');
    };
    window.addEventListener('nula-show-comment', onShow);
    return () => window.removeEventListener('nula-show-comment', onShow);
  }, []);

  // `version` is load-bearing: getThread reads the mutable Yjs comments map, so
  // we must recompute when `version` bumps (on map changes). ESLint can't see the
  // mutable read and flags it as unnecessary — it isn't.
  const thread: CommentThread | null = useMemo(
    () => (openThreadId ? getThread(doc, openThreadId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doc, openThreadId, version],
  );

  const comments = thread?.comments ?? [];

  const close = useCallback(() => {
    setOpenThreadId(null);
    setPendingRange(null);
  }, []);

  const submitReply = () => {
    const body = reply.trim();
    if (!body) return;
    if (pendingRange) {
      // First comment on a new range: create the thread, apply the mark.
      const id = createThread(doc, { author, text: body });
      editor
        .chain()
        .focus()
        .setTextSelection(pendingRange)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .command(({ commands }: any) => commands.setComment(id))
        .run();
      setPendingRange(null);
      setOpenThreadId(id);
    } else if (openThreadId) {
      addReply(doc, openThreadId, { author, text: body });
    }
    setReply('');
  };

  const onDelete = () => {
    if (!openThreadId) return;
    deleteThread(doc, openThreadId);
    // Find every range in the doc that carries this threadId and remove the mark.
    const { state } = editor;
    const { tr, doc: pmDoc } = state;
    const markType = state.schema.marks.comment;
    if (markType) {
      pmDoc.nodesBetween(0, pmDoc.content.size, (node, pos) => {
        const mark = node.marks.find(
          (m) => m.type === markType && m.attrs.threadId === openThreadId,
        );
        if (mark) {
          tr.removeMark(pos, pos + node.nodeSize, markType);
        }
      });
      editor.view.dispatch(tr);
    }
    close();
  };

  const onToggleResolved = () => {
    if (!thread) return;
    setResolved(doc, thread.id, !thread.resolved);
  };

  // Panel shows for an existing open thread OR a pending new-comment range.
  const isNew = !!pendingRange && !openThreadId;
  if (!isNew && !thread) return null;

  return (
    <FloatingPanel top={pos.top} left={pos.left} minWidth={260} className={styles.panel}>
      <Flex vertical gap="sm">
        <Flex align="center" justify="space-between">
          <Flex align="center" gap="xs">
            <Icon name="chat_bubble" size={12} className={styles.headerIcon} />
            <Text size="xs" type="secondary">
              {isNew ? 'New comment' : thread!.resolved ? 'Resolved' : 'Comment'}
            </Text>
          </Flex>
          <Flex gap="xs">
            {thread && (
              <>
                <IconButton size="sm" transparent aria-label={thread.resolved ? 'Reopen' : 'Resolve'} onClick={onToggleResolved}>
                  <Icon name={thread.resolved ? 'replay' : 'check_circle'} size={14} />
                </IconButton>
                <IconButton size="sm" transparent aria-label="Delete thread" onClick={onDelete}>
                  <Icon name="delete" size={14} />
                </IconButton>
              </>
            )}
            <IconButton size="sm" transparent aria-label="Close" onClick={close}>
              <Icon name="close" size={14} />
            </IconButton>
          </Flex>
        </Flex>

        {comments.map((c) => (
          <Flex key={c.id} vertical gap="xs" className={styles.comment}>
            <Text size="xs" type="secondary">{c.author}</Text>
            <Text size="sm">{c.text}</Text>
          </Flex>
        ))}

        <Input
          autoFocus
          size="sm"
          placeholder={isNew ? 'Add a comment… (Enter to submit)' : 'Reply… (Enter to submit)'}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); submitReply(); }
            if (e.key === 'Escape') close();
          }}
          className={styles.replyInput}
        />
      </Flex>
    </FloatingPanel>
  );
}
